import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ShoppingBagIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  CheckBadgeIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

// Mock data
const mockPurchases = [
  {
    id: 'INV-2026-00001',
    itemName: 'Equus Dualis II #11',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 11.png',
    priceCrypto: 12.5,
    priceEur: 21250,
    currency: 'ETH',
    purchaseDate: new Date(Date.now() - 3600000 * 24),
    txHash: '0xabcd1234567890abcdef1234567890abcdef1234567890abcdef1234567890ab',
    status: 'completed',
    auctionType: 'English',
    seller: '0x1234...5678',
    hasInvoice: true,
  },
  {
    id: 'INV-2026-00002',
    itemName: 'Warrior Mythology #22',
    collection: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology22.jpg',
    priceCrypto: 7.8,
    priceEur: 13260,
    currency: 'ETH',
    purchaseDate: new Date(Date.now() - 3600000 * 72),
    txHash: '0xef123456789012345678901234567890123456789012345678901234567890cd',
    status: 'completed',
    auctionType: 'Candle',
    seller: '0xabcd...ef01',
    hasInvoice: true,
  },
  {
    id: 'INV-2026-00003',
    itemName: 'Equus Dualis II #27',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 27.png',
    priceCrypto: 4.2,
    priceEur: 7140,
    currency: 'ETH',
    purchaseDate: new Date(Date.now() - 3600000 * 168),
    txHash: '0x9876543210fedcba9876543210fedcba9876543210fedcba9876543210fedcba',
    status: 'completed',
    auctionType: 'English',
    seller: '0x9876...5432',
    hasInvoice: true,
  },
];

const PurchasesPage: React.FC = () => {
  const [purchases] = useState(mockPurchases);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const truncateHash = (hash: string) => {
    return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
  };

  const totalSpent = purchases.reduce((sum, p) => sum + p.priceCrypto, 0);
  const totalSpentEur = purchases.reduce((sum, p) => sum + p.priceEur, 0);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <ShoppingBagIcon className="w-8 h-8 text-green-400" />
            Mis Compras
          </h1>
          <p className="text-gray-400">NFTs que has adquirido en subastas</p>
        </div>

        {/* Stats */}
        <div className="flex gap-4">
          <div className="bg-dark-800/50 rounded-lg px-4 py-2 border border-dark-700">
            <p className="text-xs text-gray-500">Total Items</p>
            <p className="text-xl font-bold text-white">{purchases.length}</p>
          </div>
          <div className="bg-dark-800/50 rounded-lg px-4 py-2 border border-dark-700">
            <p className="text-xs text-gray-500">Total Invertido</p>
            <p className="text-xl font-bold text-green-400">{totalSpent.toFixed(2)} ETH</p>
            <p className="text-xs text-gray-500">~€{totalSpentEur.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Purchases List */}
      {purchases.length > 0 ? (
        <div className="space-y-4">
          {purchases.map((purchase, idx) => (
            <motion.div
              key={purchase.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-start gap-4">
                  {/* Image */}
                  <div className="flex-shrink-0">
                    <img
                      src={purchase.image}
                      alt={purchase.itemName}
                      className="w-24 h-24 rounded-xl object-cover"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckBadgeIcon className="w-5 h-5 text-green-400" />
                      <span className="text-xs text-green-400">Completada</span>
                      <span className="text-xs text-gray-500">{purchase.collection}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {purchase.itemName}
                    </h3>
                    <p className="text-sm text-gray-400">
                      Comprado el {formatDate(purchase.purchaseDate)}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>Subasta: {purchase.auctionType}</span>
                      <span>Vendedor: {purchase.seller}</span>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Precio</p>
                    <p className="text-xl font-bold text-white">
                      {purchase.priceCrypto} {purchase.currency}
                    </p>
                    <p className="text-sm text-gray-400">
                      ~€{purchase.priceEur.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Transaction Info */}
                <div className="mt-4 pt-4 border-t border-dark-700 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">TX Hash:</span>
                    <code className="text-xs text-primary-400 bg-dark-900 px-2 py-1 rounded">
                      {truncateHash(purchase.txHash)}
                    </code>
                    <a
                      href={`https://polygonscan.com/tx/${purchase.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 hover:bg-dark-700 rounded transition-colors"
                      title="Ver en Polygonscan"
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-400" />
                    </a>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Factura: {purchase.id}</span>
                    <Link
                      to={`/dashboard/invoices/${purchase.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors text-sm"
                    >
                      <EyeIcon className="w-4 h-4" />
                      Ver
                    </Link>
                    <button
                      className="flex items-center gap-1 px-3 py-1.5 bg-primary-500/20 text-primary-400 hover:bg-primary-500/30 rounded-lg transition-colors text-sm"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      PDF
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <ShoppingBagIcon className="w-16 h-16 text-gray-700 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Aún no tienes compras
          </h3>
          <p className="text-gray-400 mb-6">
            Cuando ganes una subasta, tu NFT aparecerá aquí
          </p>
          <Link
            to="/explore"
            className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            Explorar Subastas
          </Link>
        </div>
      )}

      {/* Info about invoices */}
      <div className="mt-8 bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <DocumentTextIcon className="w-6 h-6 text-blue-400 flex-shrink-0" />
          <div>
            <h3 className="text-white font-semibold mb-2">Sobre las Facturas</h3>
            <p className="text-gray-400 text-sm">
              Cada compra genera una factura automáticamente con todos los datos necesarios para tu contabilidad:
            </p>
            <ul className="mt-2 space-y-1 text-gray-400 text-sm">
              <li>• Datos fiscales de NARDIHA Holdings S.A.</li>
              <li>• Hash de transacción verificable en blockchain</li>
              <li>• Precio en crypto y equivalente en EUR</li>
              <li>• Detalles del item y la subasta</li>
            </ul>
            <Link
              to="/dashboard/invoices"
              className="inline-flex items-center gap-1 mt-4 text-primary-400 hover:text-primary-300 text-sm"
            >
              Ver todas mis facturas →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchasesPage;
